import React, { useState, useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Typography,
  Grid,
  Link,
} from "@mui/material";
import Pagination from "@mui/material/Pagination";
import CallRounded from "@mui/icons-material/CallRounded";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import CustomDropdown from "../presentationals/CustomDropdown";

const CustomTable = ({
  data = [],
  headers = [],
  columnData = [],
  stickyHeader = false,
  showHeader = true,
  showPagination = true,
  initialPageSize = 10,
  pageSizeOptions = [10, 20, 50, 100],
  onPageChange,
  onPageSizeChange,
  columnAlignments = ["left", "left", "left", "left", "left", "left"],
  ...props
}) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(initialPageSize);
  const [searchTerm, setSearchTerm] = useState("");

  const tableColumnData = useMemo(() => {
    if (headers.length > 0 && columnData.length > 0) {
      return columnData.map((col, idx) => ({
        key: col,
        label: headers[idx] || col,
        align: columnAlignments[idx] || "left",
      }));
    }

    if (data.length > 0) {
      const firstItem = data[0];
      return Object.keys(firstItem).map((key, idx) => ({
        key,
        label:
          key.charAt(0).toUpperCase() +
          key
            .slice(1)
            .replace(/([A-Z])/g, " $1")
            .trim(),
        align: columnAlignments[idx] || "left",
      }));
    }

    return [];
  }, [data, headers, columnData, columnAlignments]);

  const filteredData = useMemo(() => {
    if (!searchTerm.trim()) {
      return data;
    }

    const searchLower = searchTerm.toLowerCase();
    return data.filter((item) => {
      return tableColumnData.some((column) => {
        const value = item[column.key];
        if (value === null || value === undefined) return false;
        if (typeof value === "object") {
          return JSON.stringify(value).toLowerCase().includes(searchLower);
        }
        return value.toString().toLowerCase().includes(searchLower);
      });
    });
  }, [data, searchTerm, tableColumnData]);

  const paginatedData = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return filteredData.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredData, page, rowsPerPage]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage - 1);
    onPageChange?.(newPage - 1);
  };

  const handleChangeRowsPerPage = (event) => {
    const newPageSize = parseInt(event.target.value, 10);
    setRowsPerPage(newPageSize);
    setPage(0);
    onPageSizeChange?.(newPageSize);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const totalPages = Math.ceil(filteredData.length / rowsPerPage);

  const renderCellContent = (item, column) => {
    const value = item[column.key];

    if (column.key === "Call" || column.key === "Primary No") {
      return value ? (
        <Link href={`tel:${value}`} underline="none">
          <CallRounded sx={{ color: "blue" }} />
        </Link>
      ) : (
        "-"
      );
    }

    if (value === null || value === undefined) return "-";
    if (typeof value === "object") return JSON.stringify(value);
    return value.toString();
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          mb: 2,
        }}
      >
        <div
          className="d-flex align-items-end"
          style={{
            border: "1px solid #ccc",
            borderRadius: 6,
            padding: "4px 8px",
          }}
        >
          <SearchIcon style={{ color: "#888", marginRight: 6 }} />
          <input
            type="text"
            placeholder="Search Name or Id..."
            value={searchTerm}
            onChange={handleSearchChange}
            style={{
              flex: 1,
              border: "none",
              outline: "none",
              fontSize: 14,
            }}
          />
          {searchTerm && (
            <CloseIcon
              onClick={() => setSearchTerm("")}
              style={{
                color: "#666",
                cursor: "pointer",
                marginLeft: 6,
              }}
            />
          )}
        </div>
      </Box>
      <TableContainer
        component={Paper}
        sx={{
          border: "1px solid #e0e0e0",
          borderRadius: 1,
          overflowX: "auto",
        }}
      >
        <Table stickyHeader={stickyHeader} {...props}>
          {showHeader && (
            <TableHead>
              <TableRow>
                {tableColumnData.map((column, index) => (
                  <TableCell
                    key={column.key || index}
                    align={column.align}
                    sx={{
                      fontWeight: "bold",
                      backgroundColor: "#acaccb",
                      border: "1px solid #e0e0e0",
                    }}
                  >
                    {column.label || column.key}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
          )}

          <TableBody>
            {paginatedData.length > 0 ? (
              paginatedData.map((item, rowIndex) => {
                return (
                  <TableRow
                    key={rowIndex}
                    hover
                    sx={{
                      backgroundColor: rowIndex % 2 === 0 ? "white" : "#f9f9f9",
                    }}
                  >
                    {tableColumnData.map((column, cellIndex) => (
                      <TableCell
                        key={column.key || cellIndex}
                        align={column.align} // 👈 Apply column alignment
                        sx={{
                          border: "1px solid #e0e0e0",
                          ...column.cellstyle,
                        }}
                      >
                        {column.render
                          ? column.render(item[column.key], item, rowIndex)
                          : renderCellContent(item, column)}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={tableColumnData.length || 1} align="center">
                  No data available.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {showPagination && filteredData.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: { xs: 1, sm: 2 },
                  flexWrap: "wrap",
                  justifyContent: { xs: "center", md: "flex-start" },
                }}
              >
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
                >
                  Items per page:
                </Typography>

                <Box sx={{ height: "40px", minWidth: "70px" }}>
                  <CustomDropdown
                    data={pageSizeOptions.map((size) => size.toString())}
                    value={rowsPerPage.toString()}
                    changeHandler={(value) =>
                      handleChangeRowsPerPage({
                        target: { value: parseInt(value, 10) },
                      })
                    }
                  />
                </Box>

                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{
                    fontSize: { xs: "0.75rem", sm: "0.875rem" },
                    textAlign: { xs: "center", md: "left" },
                  }}
                >
                  Showing {page * rowsPerPage + 1} to{" "}
                  {Math.min((page + 1) * rowsPerPage, filteredData.length)} of{" "}
                  {filteredData.length}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: { xs: "center", md: "flex-end" },
                }}
              >
                <Pagination
                  count={totalPages}
                  page={page + 1}
                  onChange={handleChangePage}
                  showFirstButton
                  showLastButton
                  color="primary"
                  size="medium"
                  sx={{
                    "& .MuiPaginationItem-root": {
                      fontSize: { xs: "0.75rem", sm: "0.875rem" },
                      minWidth: { xs: "28px", sm: "32px" },
                      height: { xs: "28px", sm: "32px" },
                    },
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </Box>
      )}
    </Box>
  );
};

export default CustomTable;
